import styled, { css } from "styled-components";
import { theme } from "../../../constants/StyledComponents";
import {
  TableCellAlignEnum,
  TableFixedCellAlignEnum,
  TableThemeEnum,
} from "./Table";

export const StyledTableContainer = styled.div<{ $hasPagination: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const StyledTable = styled.table<{ $theme?: TableThemeEnum }>`
  width: max-content;
  min-width: 100%;
  border-spacing: 0px;
  background-color: transparent;
  table-layout: fixed;

  tr.row-gap {
    height: 5px;
  }
`;

export const StyledHeaderScrollWrapper = styled.div`
  overflow-x: scroll;
  overflow-y: scroll;
  margin-right: 8px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const StyledScrollableTableWrapper = styled.div<{
  $height: string | number;
  $theme: TableThemeEnum;
}>`
  position: relative;
  overflow: auto;
  overscroll-behavior-x: none;

  ${({ $height }) =>
    $height &&
    css`
      height: ${typeof $height === "number" ? `${$height}px` : $height};
    `}

  ${({ $theme }) =>
    $theme === TableThemeEnum.LIGHT &&
    css`
      &::-webkit-scrollbar-track {
        border-radius: 10px;
        background-color: transparent;
        border: transparent;
      }
    `}
`;

export const StyledTableHeader = styled.th<{
  $align?: TableCellAlignEnum;
  $fixed?: TableFixedCellAlignEnum;
  $fixedShift?: number;
  $theme?: TableThemeEnum;
}>`
  position: sticky;
  top: 0;
  text-align: ${(props) => props.$align || "left"};
  padding: 12px;
  white-space: nowrap;
  overflow: hidden;
  z-index: 1;

  background-color: ${(props) =>
    props.$theme === TableThemeEnum.LIGHT
      ? theme.colors.page_background
      : theme.colors.white};

  ${(props) =>
    props.$fixed === TableFixedCellAlignEnum.LEFT &&
    css`
      z-index: 2;
      left: ${props.$fixedShift ?? 0}px;
    `}
  ${(props) =>
    props.$fixed === TableFixedCellAlignEnum.RIGHT &&
    css`
      z-index: 2;
      right: ${props.$fixedShift ?? 0}px;
    `}
`;

export const StyledTableHeaderCellContainer = styled.div<{
  $align?: TableCellAlignEnum;
}>`
  display: flex;
  flex-direction: row;
  justify-content: ${(props) =>
    props.$align === "center"
      ? "center"
      : props.$align === "right"
        ? "flex-end"
        : "flex-start"};
`;

export const StyledTableRow = styled.tr<{
  $clickable?: boolean;
  $expanded?: boolean;
}>`
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};

  & td:first-child {
    border-radius: ${({ $expanded }) =>
      $expanded
        ? `${theme.variables.border_radius_2} 0 0 0`
        : `${theme.variables.border_radius_2} 0 0 ${theme.variables.border_radius_2}`};
  }

  & td:last-child {
    border-radius: ${({ $expanded }) =>
      $expanded
        ? `0 ${theme.variables.border_radius_2} 0 0`
        : `0 ${theme.variables.border_radius_2} ${theme.variables.border_radius_2} 0`};
  }
`;

export const StyledTableData = styled.td<{
  $align?: TableCellAlignEnum;
  $widthString?: string | number;
  $theme: TableThemeEnum;
  $selected?: boolean;
  $fixed?: "left" | "right";
  $fixedShift?: number;
  $showLeftGap?: boolean;
  $showRightGap?: boolean;
}>`
  position: relative;
  text-align: ${(props) => props.$align || "left"};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  padding: 12px;

  ${(props) =>
    props.$showLeftGap &&
    css`
      &::after {
        content: "";
        position: absolute;
        right: 0px;
        top: 0;
        bottom: 0;
        width: 2px;
        background-color: ${props.$theme === TableThemeEnum.DARK
          ? theme.colors.white
          : theme.colors.page_background};
        z-index: 2;
      }
    `}

  ${(props) =>
    props.$showRightGap &&
    css`
      &::before {
        content: "";
        position: absolute;
        left: 0px;
        top: 0;
        bottom: 0;
        width: 2px;
        background-color: ${props.$theme === TableThemeEnum.DARK
          ? theme.colors.white
          : theme.colors.page_background};
        z-index: 2;
      }
    `}

  ${(props) =>
    props.$fixed &&
    css`
      position: sticky;
      z-index: 1;
      ${props.$fixed === TableFixedCellAlignEnum.LEFT
        ? css`
            left: ${props.$fixedShift ?? 0}px;
          `
        : css`
            right: ${props.$fixedShift ?? 0}px;
          `}
    `}

  background-color: ${({ $selected, $theme }) =>
    $selected
      ? theme.colors.purple_2
      : $theme === TableThemeEnum.DARK
        ? theme.colors.page_background
        : theme.colors.white};

  ${({ $widthString }) =>
    $widthString &&
    css`
      width: ${Number($widthString)
        ? `${Number($widthString)}px`
        : $widthString};
      min-width: ${Number($widthString)
        ? `${Number($widthString)}px`
        : $widthString};
      max-width: ${Number($widthString)
        ? `${Number($widthString)}px`
        : $widthString};
    `};

  & span {
    justify-content: ${(props) =>
      props.$align === "center"
        ? "center"
        : props.$align === "right"
          ? "flex-end"
          : "flex-start"};
    text-overflow: ellipsis;
  }
`;

export const StyledColGroup = styled.colgroup<{
  $cols: { width?: string | number; minWidth: string | number }[];
}>`
  ${({ $cols }) =>
    $cols.map((col, index) => {
      const w = col.width
        ? Number(col.width)
          ? `${col.width}px`
          : col.width
        : null;
      const mw = Number(col.minWidth) ? `${col.minWidth}px` : col.minWidth;
      return `
        col:nth-child(${index + 1}) {
          ${w ? `width: ${w}; max-width: ${w};` : ""}
          min-width: ${mw};
        }
      `;
    })}
`;

export const StyledEmptyContainer = styled.div`
  display: flex;
  justify-content: center;
`;

export const StyledExpandIcon = styled.div<{ $expanded: boolean }>`
  & svg {
    transition: transform 0.3s ease-in-out;
    transform: ${({ $expanded }) =>
      $expanded ? "rotate(-180deg)" : "rotate(0deg)"};
  }
`;

export const StyledExpandedCell = styled.div<{
  $theme: TableThemeEnum;
  $selected?: boolean;
}>`
  width: fit-content;
  padding: 10px;
  position: sticky;
  left: 0;
  background-color: ${({ $selected, $theme }) =>
    $selected
      ? theme.colors.purple_2
      : $theme === TableThemeEnum.DARK
        ? theme.colors.page_background
        : theme.colors.white};
  animation: expandRow 0.3s ease-in-out;
  transform-origin: top;

  @keyframes expandRow {
    from {
      opacity: 0;
      transform: scaleY(0);
    }
    50% {
      opacity: 0.8;
      transform: scaleY(1.01);
    }
    to {
      opacity: 1;
      transform: scaleY(1);
    }
  }

  border-radius: 0 0 ${theme.variables.border_radius_2}
    ${theme.variables.border_radius_2};
`;

export const StyledExpandedCellContent = styled.div<{
  $theme: TableThemeEnum;
  $selectedRow?: boolean;
  $width: number | null;
}>`
  width: ${(props) =>
    props.$width === null ? "100px" : `${props.$width - 20}px`};
  position: relative;
  margin-top: ${({ $selectedRow }) => ($selectedRow ? "" : "-10px")};
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-sizing: border-box;
  padding: 20px;
  border-radius: ${theme.variables.border_radius_2};
  background-color: ${(props) =>
    props.$theme === TableThemeEnum.DARK
      ? theme.colors.white
      : theme.colors.page_background};
  animation: appear 0.3s ease-in;

  @keyframes appear {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const StyledFooterContainer = styled.div`
  margin-top: 24px;
  display: grid;
  align-items: center;
  justify-content: end;
  width: 100%;
`;

export const StyledActionIconContainer = styled.div<{ $showAction?: boolean }>`
  display: flex;
  width: ${({ $showAction = false }) => ($showAction ? "fit-content" : "24px")};
  align-self: end;
  margin-left: auto;
`;
